<template>
    <gi-form
        v-if="form"
        ref="form"
        :form="form"
        :on-submit-fn="onSubmitFn"
        :history="history"
        :hide-actions="hideActions"
        @success="onSuccess"
        @error="onError"
        @form-error="onFormError"
        @load-history="onLoadHistory"
        @start-history-autosave="startHistoryAutosave"
        @stop-history-autosave="stopHistoryAutosave"
    >
        <template v-if="!hideHeader" #header>
            <app-page-header class="mb-4" />
        </template>
        <template #fields="{ loading }">
            <field-string ref="name" v-model="form.name" :disabled="loading" :label="$t('name')" />

            <p class="text-h5 mb-4">{{ $t('investor_fees') }}</p>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-percentage
                        ref="initial"
                        v-model="form.initial"
                        :disabled="loading"
                        :label="$t('initial_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="initial_description"
                        v-model="form.initial_description"
                        :disabled="loading"
                        :label="$t('initial_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-percentage
                        ref="annual_management"
                        v-model="form.annual_management"
                        :disabled="loading"
                        :label="$t('annual_management_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="annual_management_description"
                        v-model="form.annual_management_description"
                        :disabled="loading"
                        :label="$t('annual_management_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-percentage
                        ref="performance"
                        v-model="form.performance"
                        :disabled="loading"
                        :label="$t('performance_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="performance_description"
                        v-model="form.performance_description"
                        :disabled="loading"
                        :label="$t('performance_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>
            <field-description
                ref="performance_hurdle"
                v-model="form.performance_hurdle"
                :disabled="loading"
                :label="$t('performance_hurdle')"
            />
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-percentage
                        ref="additional"
                        v-model="form.additional"
                        :disabled="loading"
                        :label="$t('additional_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="additional_description"
                        v-model="form.additional_description"
                        :disabled="loading"
                        :label="$t('additional_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-money
                        ref="administration"
                        v-model="form.administration"
                        :disabled="loading"
                        :label="$t('administration_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="administration_description"
                        v-model="form.administration_description"
                        :disabled="loading"
                        :label="$t('administration_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-percentage
                        ref="existing_investor_initial"
                        v-model="form.existing_investor_initial"
                        :disabled="loading"
                        :label="$t('existing_investor_initial_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="existing_investor_initial_description"
                        v-model="form.existing_investor_initial_description"
                        :disabled="loading"
                        :label="$t('existing_investor_initial_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>

            <!-- -->

            <p class="text-h5 mb-4">{{ $t('investee_company_fees') }}</p>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-percentage
                        ref="investee_initial"
                        v-model="form.investee_initial"
                        :disabled="loading"
                        :label="$t('investee_initial_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="investee_initial_description"
                        v-model="form.investee_initial_description"
                        :disabled="loading"
                        :label="$t('investee_initial_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-percentage
                        ref="investee_ongoing"
                        v-model="form.investee_ongoing"
                        :disabled="loading"
                        :label="$t('investee_ongoing_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="investee_ongoing_description"
                        v-model="form.investee_ongoing_description"
                        :disabled="loading"
                        :label="$t('investee_ongoing_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" class="stacked">
                <v-col md="4" cols="12">
                    <field-percentage
                        ref="investee_additional"
                        v-model="form.investee_additional"
                        :disabled="loading"
                        :label="$t('investee_additional_fee')"
                    />
                </v-col>
                <v-col md="8" cols="12">
                    <field-string
                        ref="investee_additional_description"
                        v-model="form.investee_additional_description"
                        :disabled="loading"
                        :label="$t('investee_additional_fee') + ' ' + $t('description')"
                    />
                </v-col>
            </v-row>
        </template>
        <template v-if="prependCancelButton" #prepend-actions>
            <app-button-cancel @click="onCancel" />
        </template>
    </gi-form>
</template>

<script>
import form from '@/mixins/form';
import history from '@/mixins/history';
import { Fees } from '@/models';

export default {
    name: 'CreateFee',
    components: {
        GiForm: () =>
            import(
                /* webpackChunkName: "forms" */
                '@/components/forms/Form'
            )
    },
    mixins: [form, history],
    data: () => ({
        id: 'CreateFee',
        model: Fees
    })
};
</script>
